import {
  ActionLink,
  LearningShell,
  LoopRail,
  RetentionToolsPanel,
  SkillRailsBand,
  ToneBadge,
} from '../features/learning-space/components';
import { useSearchParams } from 'react-router-dom';
import {
  buildExamWorkspacePageModel,
  buildLearningRouteScope,
  resolveExamSpaceTheme,
  type LearnerNextStep,
  type PageAction,
} from '@examflow/learning-space';
import { buildWorkspaceRetentionTools } from '../features/learning-space/retentionToolsModel';
import { useLearningViewer } from '../features/learning-space/useLearningViewer';
import { useAbilitySnapshots } from '../features/learning-space/useAbilitySnapshots';
import {
  buildLearningEvidenceRecordQueryScope,
  useRecentTrainingAttempts,
} from '../features/learning-space/useRecentTrainingAttempts';
import { useAccessibleBanks } from '../hooks/useApiQueries';
import { buildPlatformScope } from '@examflow/core';
import { usePlatformSettings } from '../features/platform/PlatformSettingsContext';

function workspaceNextStepKindLabel(nextStepId: LearnerNextStep['id']): string {
  switch (nextStepId) {
    case 'resolve-content-gap':
      return '内容门禁';
    case 'start-ability-diagnosis':
      return '入门诊断';
    case 'remediate-latest-errors':
      return '错题优先';
    case 'digest-mock-snapshot':
      return '消化模考';
    case 'start-mock-exam':
      return '进入模考';
    case 'continue-prescription-training':
      return '训练处方';
    case 'follow-ability-prescription':
    case 'review-result-errors':
    case 'continue-result-prescription':
      return '训练处方';
    default: {
      const _exhaustive: never = nextStepId;
      void _exhaustive;
      return '训练处方';
    }
  }
}

function mockBandStatusLabel(input: {
  mockBand: 'progress' | 'confirm';
  shellStatusKind: string;
}): string {
  if (input.mockBand === 'confirm') return '可模考';
  if (input.shellStatusKind === 'mock_digestion') return '消化中';
  return '积累中';
}

function mockCredibilityLabel(
  credibility: 'official_counts' | 'reference_only' | 'unconfigured'
): string | null {
  switch (credibility) {
    case 'official_counts':
      return '题量对齐本场考试';
    case 'reference_only':
      return '按考试结构参考';
    case 'unconfigured':
      return '模考结构待完善';
    default: {
      const _exhaustive: never = credibility;
      void _exhaustive;
      return null;
    }
  }
}

export default function ExamWorkspacePage() {
  const [searchParams] = useSearchParams();
  const routeScope = buildLearningRouteScope(searchParams);
  const { learnerGates } = usePlatformSettings();
  const viewer = useLearningViewer(routeScope);
  const activeScope = buildPlatformScope({ examType: viewer.activeExamSpace.examCode });
  const evidenceScope = buildLearningEvidenceRecordQueryScope(viewer);
  const trainingAttempts = useRecentTrainingAttempts({
    userId: viewer.userId,
    ...evidenceScope,
    limit: 30,
  });
  const abilitySnapshots = useAbilitySnapshots({
    userId: viewer.userId,
    examType: viewer.activeExamSpace.examCode,
    limit: 20,
  });
  const accessibleBanks = useAccessibleBanks(activeScope ?? undefined);
  const model = buildExamWorkspacePageModel(
    viewer,
    trainingAttempts.records,
    {
      accessibleBanks: accessibleBanks.data,
      accessibleBanksStatus: accessibleBanks.isLoading
        ? 'loading'
        : accessibleBanks.isError
          ? 'unavailable'
          : 'ready',
    },
    {
      abilitySnapshots:
        abilitySnapshots.status === 'ready' ? abilitySnapshots.snapshots : [],
      trainingEvidenceStatus: trainingAttempts.status,
    }
  );
  const contentIsReady = model.contentReadiness.status === 'ready';
  const evidenceReady = trainingAttempts.status === 'ready';
  const evidenceLoading = trainingAttempts.status === 'loading';
  const evidenceUnavailable = trainingAttempts.status === 'unavailable';
  const showEvidenceGate = contentIsReady && !evidenceReady;
  const theme = resolveExamSpaceTheme(viewer.activeExamSpace.examCode);
  const nextStepKindLabel = workspaceNextStepKindLabel(model.nextStep.id);
  const mockProgress = model.mockExamSummary.progress;
  const credibilityLabel = mockCredibilityLabel(model.mockExamSummary.credibility);
  const showMockBand =
    contentIsReady &&
    evidenceReady &&
    (model.chrome.mockBand === 'progress' || model.chrome.mockBand === 'confirm');

  const contentRetryAction =
    model.contentReadiness.primaryAction.id === 'content-readiness-unavailable';
  const primaryAction: PageAction = showEvidenceGate
    ? evidenceLoading
      ? {
          id: 'training-evidence-loading',
          label: '读取中',
          kind: 'secondary',
          disabled: true,
        }
      : {
          id: 'retry-training-evidence',
          label: '重新加载',
          kind: 'secondary',
        }
    : contentIsReady
      ? model.nextStep.action
      : model.contentReadiness.primaryAction;

  const handlePrimaryActionClick = () => {
    if (primaryAction.id === 'retry-training-evidence') {
      void trainingAttempts.reload();
      return;
    }
    if (primaryAction.id === 'content-readiness-unavailable' || contentRetryAction) {
      void accessibleBanks.refetch();
    }
  };

  const heroTitle = showEvidenceGate
    ? evidenceLoading
      ? '正在读取训练记录'
      : '训练记录暂时不可用'
    : contentIsReady
      ? model.nextStep.title
      : model.contentReadiness.title;
  const heroDescription = showEvidenceGate
    ? evidenceLoading
      ? '正在确认你在本场考试的训练证据，完成后才会给出下一步。'
      : '暂时无法读取训练证据。请重试；在恢复前不会按「零档案」推进入门诊断。'
    : contentIsReady
      ? model.nextStep.description
      : model.contentReadiness.description;

  return (
    <LearningShell
      viewer={viewer}
      activeNav="workspace"
      statusLabel={
        evidenceLoading
          ? '读取训练记录'
          : evidenceUnavailable
            ? '训练记录不可用'
            : model.chrome.shellStatusLabel
      }
    >
      <section
        className="relative"
        data-testid="exam-space-workspace-hero"
        data-structure-shape={model.profile.structureShape}
      >
        <p
          className="text-xs font-semibold tracking-[0.14em]"
          style={{ color: theme.accent }}
        >
          {model.profile.copy.structureCue}
        </p>
        <h1
          className="mt-3 max-w-3xl font-black tracking-tight text-[#111827]"
          style={{ fontSize: 'clamp(2.5rem, 6vw, 3.75rem)', lineHeight: 1.05 }}
        >
          {heroTitle}
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">{heroDescription}</p>
        {!showEvidenceGate && (
          <p className="mt-4 max-w-2xl text-sm text-slate-600" data-testid="exam-space-ability-line">
            <span className="font-semibold text-[#111827]">{model.chrome.abilityLine.displayLevel}</span>
            <span className="mx-1.5 text-slate-300">·</span>
            <span>{model.chrome.abilityLine.stabilityLabel}</span>
            <span className="mx-1.5 text-slate-300">·</span>
            <span>{model.chrome.abilityLine.weakestDimensionLabel}</span>
          </p>
        )}
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <ActionLink action={primaryAction} onClick={handlePrimaryActionClick} />
          {contentIsReady &&
            evidenceReady &&
            model.writingFocus &&
            model.writingFocus.primaryAction.href !== primaryAction.href && (
              <ActionLink action={model.writingFocus.primaryAction} />
            )}
          {contentIsReady && evidenceReady && (
            <ToneBadge tone={model.nextStep.tone}>{nextStepKindLabel}</ToneBadge>
          )}
        </div>
        {contentIsReady && evidenceReady && model.writingFocus && (
          <p className="mt-3 max-w-2xl text-sm text-slate-500" data-testid="exam-space-writing-focus">
            {model.writingFocus.reason}
          </p>
        )}
        <div className="mt-8">
          <LoopRail steps={model.currentPath} accent={theme.accent} />
        </div>
      </section>

      {contentIsReady && (
        <SkillRailsBand
          rails={model.skillRails}
          accent={theme.accent}
          highlightedModuleId={model.chrome.highlightedModuleId}
          title={model.profile.copy.skillMapTitle}
          subtitle={model.profile.copy.skillMapSubtitle}
          structureShape={model.profile.structureShape}
        />
      )}

      {showMockBand && (
        <section className="mt-10" data-testid="exam-space-mock-progress">
          <div className="rounded-md border border-white/70 bg-white/45 px-4 py-4">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h2 className="text-sm font-semibold text-slate-700">{model.mockExamSummary.title}</h2>
              <span className="text-xs font-medium text-slate-500">
                {mockBandStatusLabel({
                  mockBand: model.chrome.mockBand === 'confirm' ? 'confirm' : 'progress',
                  shellStatusKind: model.chrome.shellStatusKind,
                })}
                {credibilityLabel ? (
                  <>
                    <span className="mx-1.5 text-slate-300">·</span>
                    {credibilityLabel}
                  </>
                ) : null}
              </span>
            </div>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              {model.mockExamSummary.description}
            </p>
            <p className="mt-3 text-xs text-slate-500">
              专项 {mockProgress.sessionCount}/{mockProgress.sessionRequired} 组
              <span className="mx-1.5 text-slate-300">·</span>
              有效作答 {mockProgress.answeredCount}/{mockProgress.answeredRequired} 题
            </p>
          </div>
        </section>
      )}

      <RetentionToolsPanel tools={buildWorkspaceRetentionTools(routeScope, learnerGates)} />
    </LearningShell>
  );
}
